import { BRAND } from "constants/brand";
import ChangeVendorPassword from "components/Vendors/ChangeVendorPassword/ChangeVendorPassword";
import "./addVendor.scss";
import { Switch } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";

import { Ring } from "@uiball/loaders";
import { useNavigate, useParams } from "react-router-dom";
import useVendors from "hooks/useVendors";
import FieldHelp from "components/Ui/FieldHelp/FieldHelp";
import profileImage from "assets/images/profile.jpg";
import coverImage from "assets/images/notfound.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import useArea from "hooks/useArea";
import AreaCost from "../AddVendorPage/AreaCost/AreaCost";
import { readApiError } from "utils/apiError";
import {
    areaRowsProblem,
    isNewRow,
    newRowId,
    removeRow,
    toCostPayload,
    upsertRow,
} from "../areaCostRows";
import useVendorCategories from "hooks/useVendorCategories";
import Select from "components/Ui/SelectField/SelectField";
import useCity from "hooks/useCity";
import { useSelector } from "react-redux";
import { moveVendorCity } from "services/vendors";

/**
 * The delivery price, in ₪, that every area of the new city gets when a
 * restaurant moves. The admin edits single areas after the move.
 */
const MOVE_DEFAULT_DELIVERY_COST = 10;

const EditVendor = (props) => {
    const { loading, updateVendor, error, addDeliveryCost, getVendor, deleteArea } = useVendors();
    const { categories } = useVendorCategories();
    const [newCategories, setNewCategories] = useState([]);
    const [seletedCategories, setSelectedCategories] = useState([]);
    const [image, setImage] = useState(profileImage);
    const [cover, setCover] = useState(coverImage);
    const [areaNumber, setAreaNumber] = useState([]);
    const [vendorAreas, setVendorArea] = useState([]);
    // The ids of the new area rows: see ../areaCostRows.js.
    const rowIdCounter = useRef(0);
    const [city, setCity] = useState("");
    // The city the restaurant is in now. A different `city` means a move.
    const [savedCity, setSavedCity] = useState("");
    const [moveCost, setMoveCost] = useState(String(MOVE_DEFAULT_DELIVERY_COST));
    const [reloadKey, setReloadKey] = useState(0);
    const adminInfo = useSelector((state) => state.auth.userData);
    const canMoveCity = adminInfo?.super_admin === true;
    // The same rule as the route of the offers page (App.js).
    const canManageOffers = !!(adminInfo?.super_admin || adminInfo?.roles?.manage_delivery_offers);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [vendor, setVendor] = useState([]);
    const [orderMethod, setOrderMethod] = useState("cart");
    const [vendorType, setVendorType] = useState("restaurant");
    const [vendorStatus, setVendorStatus] = useState("open");
    const [isServiceVendor, setIsServiceVendor] = useState(false);
    const { areas } = useArea();
    const form = useRef();
    const imageRef = useRef();
    const coverRef = useRef();
    const navigate = useNavigate();
    const { id } = useParams();
    const { cities } = useCity();

    const handleChange = (selectedOptions) => {
        setSelectedCategories(selectedOptions);
    };

    const deleteAreaHandler = async (id) => {
        // Only a saved row is in the database. A new row had the length of
        // the list as its id, and this call asked the API to delete the
        // `delivery_costs` row with that number: a row of another restaurant.
        if (!isNewRow(id)) {
            await deleteArea(id);
        }

        setAreaNumber((rows) => removeRow(rows, id));
        // A row that changed and was then deleted must not be saved again.
        setVendorArea((rows) => removeRow(rows, id));
    };

    useEffect(() => {
        const fetchVendor = async () => {
            try {
                const response = await getVendor(id);

                if (response) {
                    setVendor(response.results);
                    setCity(response.results?.city?.id ?? "");
                    setSavedCity(response.results?.city?.id ?? "");
                    // The area rows are rebuilt from the response, so a change
                    // kept from before a reload must not be saved again.
                    setVendorArea([]);
                    setOrderMethod(response.results?.email || "cart");
                    setVendorType(response.results?.type || "restaurant");
                    setVendorStatus(response.results?.status || "open");
                    // Read `isServiceVendor`, write `is_service_vendor` — the
                    // read is the shared client-facing name, the write is the
                    // column, which editVendor mass-assigns.
                    setIsServiceVendor(!!response.results?.isServiceVendor);
                    setImage(response.results.image || profileImage);
                    setCover(response.results.cover || coverImage);

                    setSelectedCategories(
                        response.results.vendorCategories.map((category) => {
                            return { value: category.id, label: category.name };
                        }),
                    );
                    const areasInputs = response.results.areas.map((area) => ({
                        id: area.delivery_cost.id,
                        area: area,
                    }));
                    setAreaNumber(areasInputs);
                }
            } catch (error) {
                console.log(error);
            }
        };
        if (categories) {
            setNewCategories(
                categories.map((category) => {
                    return { value: category.id, label: category.name };
                }),
            );
        }
        fetchVendor();
    }, [id, categories, areas, reloadKey]);

    const cityChanged = Boolean(savedCity) && Boolean(city) && String(city) !== String(savedCity);
    const cityName = (cityId) => cities?.find((c) => String(c.id) === String(cityId))?.name ?? "";

    const getAreaCost = (data) => {
        setVendorArea((rows) => upsertRow(rows, data));
    };

    const submitHandler = async (e) => {
        e.preventDefault();

        // Each row on screen with its value now: the changed value, or the
        // saved one. A move to another city does not save the rows.
        const rowsOnScreen = areaNumber.map(
            (item) =>
                vendorAreas.find((row) => String(row.id) === String(item.id)) ?? {
                    id: item.id,
                    area: item.area?.id ?? 0,
                },
        );
        const rowsProblem = cityChanged ? null : areaRowsProblem(rowsOnScreen, areas);
        if (rowsProblem) {
            props.showMessage("error", "هناك خطأ", rowsProblem);
            return;
        }

        const vendorData = new FormData();

        seletedCategories.forEach((category) => {
            vendorData.append("categories[]", category.value);
        });
        if (imageRef.current.value) {
            vendorData.append("image", imageRef.current.files[0], imageRef.current.files[0].name);
        }
        if (coverRef.current.value) {
            vendorData.append("cover", coverRef.current.files[0], coverRef.current.files[0].name);
        }

        // READ EACH FIELD BY ITS NAME, NEVER BY ITS POSITION. This form read
        // `form.current[6]` and `form.current[11]`. Two ⓘ help buttons were
        // added above them on 2026-07-25, and a button is a form element too.
        // So position 6 became a checkbox and position 11 a button: every
        // save wrote the word "on" into the map link, and the city was never
        // sent, so the page could not move a restaurant to another city.
        //
        // By the `name` attribute only. `elements.namedItem` also matches an
        // `id`, and this form has two inputs with the id "address" and two
        // with the id "phone", so it returned a list with an empty value.
        const field = (name) => form.current.querySelector(`[name="${name}"]`)?.value ?? "";

        vendorData.append("id", vendor.id);
        vendorData.append("name", field("name"));
        vendorData.append("phone", field("phone"));
        vendorData.append("address", field("address"));
        vendorData.append("delivery_time", field("delivery_time"));
        vendorData.append("is_service_vendor", isServiceVendor);
        vendorData.append("direction", field("direction"));
        // LEGACY: the order method is stored in users.email — the column holds
        // "cart" or "phone" for every vendor (262/15 in production), never a real
        // address. Nothing reads it any more: the customer app's call-based shops
        // section was removed, and neither the api nor any app branches on it.
        // Kept because changing it means a data migration off the email column.
        vendorData.append("email", orderMethod);
        vendorData.append("type", vendorType);
        vendorData.append("status", vendorStatus);
        // THE CITY IS NOT PART OF THIS UPDATE. A move goes through
        // PATCH /vendor/:userId/city, which moves the delivery prices with it.
        // Sent here, the city changed alone and the restaurant kept prices for
        // the areas of its old city only, so no customer of the new city could
        // check out from it.
        let moved = null;
        if (cityChanged) {
            const cost = Number(moveCost);
            if (moveCost === "" || !Number.isFinite(cost) || cost < 0) {
                props.showMessage("error", "هناك خطأ", "اكتب سعر توصيل صحيحًا للمناطق الجديدة");
                return;
            }
            try {
                moved = await moveVendorCity(vendor.id, city, cost);
            } catch (err) {
                props.showMessage(
                    "error",
                    "تعذر نقل المطعم",
                    err.response?.data?.message || "حدث خطأ غير متوقع",
                );
                return;
            }
        }

        try {
            const response = await updateVendor(vendorData);

            // After a move, the area rows on screen belong to the old city:
            // they are not saved, and the page loads the new ones below.
            const costsRes = moved
                ? { results: [] }
                : await addDeliveryCost({
                      vendorId: vendor.id,
                      costs: vendorAreas.map(toCostPayload),
                  });

            // The restaurant is saved, and the API refused its delivery prices:
            // say which, with the API's own sentence.
            const costsFailure = readApiError(costsRes, "تعذر حفظ أسعار التوصيل");
            if (response.name && costsFailure) {
                props.showMessage("error", "حُفظ المطعم، ولم تُحفظ أسعار التوصيل", costsFailure);
                return;
            }

            if (response.name && costsRes.results) {
                props.showMessage(
                    "success",
                    "تم التعديل",
                    moved
                        ? `نُقل المطعم إلى ${cityName(moved.toCityId)}. أُضيفت ${moved.addedCosts} منطقة بسعر ${Number(moveCost)} ₪${moved.keptCosts ? ` وبقي سعر ${moved.keptCosts} منطقة كان محددًا` : ""}، وحُذفت ${moved.removedCosts} من أسعار المدينة القديمة. عدّل الأسعار من قائمة المناطق.`
                        : "تم تعديل المطعم بنجاح",
                );
                if (moved) setReloadKey((key) => key + 1);
                // navigate("/vendors");
            } else {
                props.showMessage("error", "هناك خطأ", response.message || "حدث خطأ غير متوقع");
            }
        } catch (err) {
            console.error("Error adding vendor:", err);
            props.showMessage("error", "هناك خطأ", error || "حدث خطأ غير متوقع");
        }
    };

    return (
        <div className={`add-vendor`}>
            {loading ? (
                <div className="loading-spinner center">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : null}
            <h3 className="text-center" style={{ marginBottom: "20px" }}>
                تعديل مطعم
            </h3>

            <div className="images">
                <div className="image-input">
                    <FontAwesomeIcon
                        onClick={() => {
                            setImage(profileImage);
                            imageRef.current.value = "";
                        }}
                        icon={faTrashCan}
                        size="lg"
                        color="red"
                    />
                    <label htmlFor="image">
                        <img src={image} alt="profile" />
                    </label>
                    <input
                        type="file"
                        ref={imageRef}
                        onChange={(e) => {
                            if (e.target.files[0]) {
                                setImage(URL.createObjectURL(e.target.files[0]));
                            }
                        }}
                        accept=".png,.jpg,.jpeg,.svg,.webp"
                        name="image"
                        id="image"
                    />
                </div>
                <div className="image-input">
                    <FontAwesomeIcon
                        onClick={() => {
                            setCover(coverImage);
                            coverRef.current.value = "";
                        }}
                        icon={faTrashCan}
                        size="lg"
                        color="red"
                    />
                    <label htmlFor="cover">
                        <img src={cover} alt="cover" />
                    </label>
                    <input
                        ref={coverRef}
                        type="file"
                        onChange={(e) => {
                            if (e.target.files[0]) {
                                setCover(URL.createObjectURL(e.target.files[0]));
                            }
                        }}
                        accept=".png,.jpg,.jpeg,.svg,.webp"
                        name="cover"
                        id="cover"
                    />
                </div>
            </div>
            <form ref={form} className="row row-cols-1" onSubmit={submitHandler}>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="name">
                        <Form.Label>
                            اسم المطعم<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Control
                            name="name"
                            type="text"
                            defaultValue={vendor?.name}
                            placeholder="اسم المطعم"
                            required
                        />
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="phone">
                        <Form.Label>
                            رقم الهاتف<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Control
                            name="phone"
                            defaultValue={vendor?.phone}
                            type="text"
                            placeholder="رقم الهاتف"
                            required
                        />
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="address">
                        <Form.Label>
                            العنوان<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Control
                            name="address"
                            defaultValue={vendor?.address}
                            type="text"
                            placeholder="العنوان"
                            required
                        />
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="phone">
                        <Form.Label>
                            وقت التوصيل<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Control
                            name="delivery_time"
                            type="number"
                            placeholder="وقت التوصيل"
                            min={0}
                            defaultValue={vendor?.delivery_time}
                            required
                        />
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    {/* Saves to vendors.is_service_vendor via the vendor update's
                        body spread — no service change needed. Operator-facing
                        explanation lives in the FieldHelp below. */}
                    <Form.Group className="mb-3" controlId="isServiceVendor">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Form.Label style={{ marginBottom: 0 }}>
                                مطعم خدمات (اطلب اي شيء)
                            </Form.Label>
                            <FieldHelp label="عن مطعم الخدمات">
                                حساب توصيل بلا منتجات — الزبون يطلب منه أي شيء بدل اختيار أصناف
                                (مثل: تكسي طلباتك، مكاتب اطلب اي غرض).
                                <br />
                                ⚠️ تطبيق الزبون ما زال يعتمد قائمة ثابتة، فالتفعيل لا يغيّر شيئاً
                                للزبائن حتى يعتمد التطبيق الحقل الجديد.
                            </FieldHelp>
                            <Switch
                                color="warning"
                                size="small"
                                checked={isServiceVendor}
                                onChange={(e) => setIsServiceVendor(e.target.checked)}
                            />
                        </div>
                    </Form.Group>
                </div>

                {canManageOffers ? (
                    <div className="col-md-4 col-lg-4">
                        <Form.Group className="mb-3" controlId="free">
                            <Form.Label>التوصيل المجاني وعروض التوصيل</Form.Label>
                            {/* A free delivery is a delivery offer. This opens
                                the add form of the offers page, with a free
                                delivery for this restaurant filled in. */}
                            <button
                                type="button"
                                className="btn btn-outline-secondary w-100"
                                onClick={() =>
                                    navigate("/delivery-offers", {
                                        state: {
                                            preset: "vendor_free",
                                            cityId: savedCity,
                                            vendor: {
                                                value: Number(id),
                                                label: vendor?.name ?? "",
                                            },
                                        },
                                    })
                                }
                            >
                                إضافه توصيل مجاني لهذا المطعم
                            </button>
                        </Form.Group>
                    </div>
                ) : null}
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="address">
                        <Form.Label>
                            رابط الخريطه<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Control
                            name="direction"
                            type="text"
                            placeholder="رابط الخريطه"
                            defaultValue={vendor?.direction}
                            required
                        />
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="type">
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <Form.Label style={{ marginBottom: 0 }}>
                                طريقه الطلب<span style={{ color: "red" }}>*</span>
                            </Form.Label>
                            <FieldHelp label="عن طريقة الطلب">
                                هل يطلب الزبون من المتجر عبر السلة أم بالاتصال؟
                                <br />
                                ⚠️ قسم «المحلات بالاتصال» أُزيل من تطبيق الزبون، ولا يوجد اليوم أي
                                شيء يقرأ هذا الخيار — تغييره لا يؤثر على شيء.
                                <br />
                                يُحفظ في حقل البريد الإلكتروني للحساب، وليس بريداً فعلياً.
                            </FieldHelp>
                        </div>
                        <Form.Select
                            required
                            value={orderMethod}
                            onChange={(e) => setOrderMethod(e.target.value)}
                        >
                            <option value={"cart"}>السله</option>
                            <option value={"phone"}>الهاتف</option>
                        </Form.Select>
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="status">
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <Form.Label style={{ marginBottom: 0 }}>
                                النوع<span style={{ color: "red" }}>*</span>
                            </Form.Label>
                            <FieldHelp label="عن النوع">
                                يحدّد مكان ظهور المتجر للزبون فقط.
                                <br />
                                اختيار «سوبر ماركت» لا يجعله مطعم خدمات — استخدم مفتاح «مطعم خدمات»
                                أعلاه لذلك.
                            </FieldHelp>
                        </div>
                        <Form.Select
                            required
                            value={vendorType}
                            onChange={(e) => setVendorType(e.target.value)}
                        >
                            <option value={"restaurant"}>مطعم</option>
                            <option value={"supermarket"}>سوبر ماركت</option>
                        </Form.Select>
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="status">
                        <Form.Label>
                            الحاله<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Select
                            required
                            value={vendorStatus}
                            onChange={(e) => setVendorStatus(e.target.value)}
                        >
                            <option value={"close"}>مغلق</option>
                            {/* Disabled while the wallet keeps the restaurant
                                closed (api#147): the server refuses it, and a
                                save that sent it would fail as a whole. */}
                            <option value={"open"} disabled={vendor?.wallet?.blocked === true}>
                                مفتوح
                            </option>
                            <option value={"soon"}>قريبا</option>
                            <option value={"busy"}>مشغول</option>
                        </Form.Select>
                        {vendor?.wallet?.blocked === true ? (
                            <Form.Text className="text-danger">
                                {vendor.wallet.blocked_reason}. يجب أن يكون رصيد المحفظة أكبر من صفر
                                لفتح المطعم.
                            </Form.Text>
                        ) : null}
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="status2">
                        <Form.Label>
                            التصنيف<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Select
                            value={seletedCategories}
                            options={newCategories}
                            isMulti
                            onChange={handleChange}
                        />
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="status">
                        <Form.Label>
                            المدينه<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Select
                            value={city}
                            disabled={!canMoveCity}
                            title={
                                canMoveCity ? undefined : "نقل المطعم لمدينة أخرى للمشرف العام فقط"
                            }
                            onChange={(e) => {
                                setCity(e.target.value);
                            }}
                        >
                            {/* Without this the select LOOKS like the first city
                                is chosen while its value is still "", which is
                                how a blank cityId reached the API. */}
                            <option value="">اختر المدينه</option>
                            {cities?.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>
                {/* The password inputs used to live here. They were submitted on
                    EVERY save, and an empty one wiped the stored hash — see
                    ChangeVendorPassword for the full story. A password now only
                    leaves the browser from its own modal. */}
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3">
                        <Form.Label>كلمه المرور</Form.Label>
                        <button
                            type="button"
                            className="cancel"
                            style={{ display: "block", width: "100%" }}
                            onClick={() => setShowPasswordModal(true)}
                        >
                            تغيير كلمة المرور
                        </button>
                    </Form.Group>
                </div>
                <div>
                    <button
                        style={{ margin: "40px 0 0 10px", minWidth: "fit-content" }}
                        className="button"
                        onClick={() => {
                            setAreaNumber((rows) => [...rows, { id: newRowId(rowIdCounter) }]);
                        }}
                        type="button"
                    >
                        إضافه منطقه
                    </button>
                    {/* <button
            style={{ marginTop: "40px", background: "red", color: "#fff" }}
            disabled={areaNumber.length === 0 ? true : false}
            onClick={() => {
              setAreaNumber((pre) => (pre = pre.slice(0, pre.length - 1)));
              setVendorArea((pre) => (pre = pre.slice(0, pre.length - 1)));
            }}
            type="button">
            حذف منطقه
          </button> */}
                </div>
                {cityChanged ? (
                    // While a move is pending, the rows below belong to the old
                    // city. They are hidden, and the notice says what the save
                    // does instead.
                    <div className="form-warning vendor-city-move">
                        <p>
                            سيُنقل المطعم من «{cityName(savedCity)}» إلى «{cityName(city)}» عند
                            الحفظ. ستُحذف أسعار التوصيل لمناطق المدينة القديمة، وتأخذ كل مناطق
                            المدينة الجديدة هذا السعر. يمكنك تعديل كل منطقة بعد الحفظ.
                        </p>
                        <Form.Group controlId="moveCost" className="vendor-city-move__cost">
                            <Form.Label>سعر التوصيل للمناطق الجديدة (₪)</Form.Label>
                            <Form.Control
                                type="number"
                                min={0}
                                step="0.5"
                                inputMode="decimal"
                                value={moveCost}
                                onChange={(e) => setMoveCost(e.target.value)}
                            />
                        </Form.Group>
                        <p className="vendor-city-move__note">
                            إذا لم يكن للمطعم نسبة عمولة خاصة، يأخذ النسبة الافتراضية للمدينة
                            الجديدة.
                        </p>
                    </div>
                ) : null}
                {(cityChanged ? [] : areaNumber)?.map((item) => {
                    return (
                        <AreaCost
                            index={item?.id}
                            getAreaCost={getAreaCost}
                            areas={
                                city === "" || !city
                                    ? areas
                                    : areas.filter((area) => +area.cityId === +city)
                            }
                            key={item.id}
                            area={item?.area}
                            deleteArea={deleteAreaHandler}
                        />
                    );
                })}
                <div className="submit">
                    <button
                        style={{
                            marginTop: "20px",
                            width: "250px",
                            fontWeight: "bold",
                            float: "left",
                        }}
                        className="button"
                        type="submit"
                    >
                        حفظ التعديلات
                    </button>
                </div>
            </form>
            <ChangeVendorPassword
                show={showPasswordModal}
                close={() => setShowPasswordModal(false)}
                showMessage={props.showMessage}
                vendorId={id}
                vendorName={vendor?.name}
            />
        </div>
    );
};
export default EditVendor;
